import { FC, PropsWithChildren, useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import {
  findRestoredAuthSet,
  useCompanyScopeSelect,
  useSessionStore,
  useSimpleNavigation,
} from "@ryde/redux";
import {
  companyIdSelector,
  isLoggedInAtom,
  Log,
  profileAuthorizedGroupedInSelector,
  ProfileStorage,
  RoutePaths,
  trimStartSlashes,
} from "@ryde/shared";

import { buildCompanyRedirectPathSuffix } from "./company-redirect-wrapper.utls";

interface ICompanyRedirectWrapper extends PropsWithChildren {
  basePath: string;
  isRoutingWithId?: boolean;
  /** Carry the `*` sub-path over to the company-scoped URL (deep links under `basePath`). */
  shouldKeepSubPath?: boolean;
}

export const CompanyRedirectWrapper: FC<ICompanyRedirectWrapper> = ({
  children,
  basePath,
  isRoutingWithId,
  shouldKeepSubPath,
}) => {
  const currentCompanyId = useRecoilValue(companyIdSelector);
  const isLoggedIn = useRecoilValue(isLoggedInAtom);
  const authSetsByCompany = useRecoilValue(profileAuthorizedGroupedInSelector);
  const { applyCompanyScope } = useCompanyScopeSelect();
  const { rebootSession } = useSessionStore();
  const { navigateLogin } = useSimpleNavigation();
  const { companyId: routeCompanyId, id, "*": subPath } = useParams();

  const routeCompanyIdNum = Number(routeCompanyId);

  const isProfileModalBackgroundPage = useMemo(() => {
    return !currentCompanyId && (!basePath || basePath === RoutePaths.HOME);
  }, [basePath, currentCompanyId]);

  const isPageAccessedWithoutCompanyIdChosen = useMemo(() => {
    return !currentCompanyId && basePath && basePath !== RoutePaths.HOME;
  }, [basePath, currentCompanyId]);

  const hasAccessToRouteCompany = useMemo(() => {
    if (!routeCompanyIdNum || !authSetsByCompany) {
      return false;
    }
    const sets = authSetsByCompany[routeCompanyIdNum];
    return Array.isArray(sets) && sets.length > 0;
  }, [authSetsByCompany, routeCompanyIdNum]);

  useEffect(() => {
    if (!isLoggedIn) {
      Log.debug("User not logged in, navigating to login");
      navigateLogin();
    }
  }, [isLoggedIn, navigateLogin]);

  useEffect(() => {
    if (
      !isLoggedIn ||
      !routeCompanyIdNum ||
      currentCompanyId === routeCompanyIdNum ||
      !hasAccessToRouteCompany ||
      !authSetsByCompany
    ) {
      return;
    }

    let isCancelled = false;

    const authSetsByRouteCompany = authSetsByCompany[routeCompanyIdNum] ?? [];
    const storedAuthSet = ProfileStorage.authSet;
    const firstAuthSet = authSetsByRouteCompany[0];
    const matchedStoredAuthSet = findRestoredAuthSet({
      authSets: authSetsByRouteCompany,
      stored: storedAuthSet,
    });
    const candidateAuthSet = matchedStoredAuthSet ?? firstAuthSet;

    if (!candidateAuthSet) {
      return;
    }

    const isPreselected = !!matchedStoredAuthSet;
    Log.debug(
      `URL company switch: set scope to company ${routeCompanyIdNum} (chosen ${isPreselected ? "PRESELECTED (storedAuthSet)" : "FIRST (firstAuthSet)"})`,
    );

    void applyCompanyScope(candidateAuthSet, () => {
      if (!isCancelled) {
        rebootSession();
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [
    applyCompanyScope,
    authSetsByCompany,
    currentCompanyId,
    hasAccessToRouteCompany,
    isLoggedIn,
    rebootSession,
    routeCompanyIdNum,
  ]);

  if (
    currentCompanyId &&
    routeCompanyIdNum &&
    currentCompanyId === routeCompanyIdNum
  ) {
    /* everything OK, just render page  */
    return children;
  }

  if (isProfileModalBackgroundPage) {
    /* render background (blank) page for profile modal dialog  */
    return children;
  }

  if (isPageAccessedWithoutCompanyIdChosen) {
    Log.warn("No company id loaded for page for some reason");
    return children;
  }

  if (hasAccessToRouteCompany) {
    return null;
  }

  const pathSuffix = buildCompanyRedirectPathSuffix({
    routeId: isRoutingWithId ? id : undefined,
    subPath: shouldKeepSubPath ? subPath : undefined,
  });
  const urlWithCompany = basePath?.trim()
    ? `/c/${currentCompanyId}/${trimStartSlashes(basePath)}${pathSuffix}`
    : `/c/${currentCompanyId}`;

  return <Navigate to={urlWithCompany} replace />;
};
